import { useState } from 'react'
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '../../lib/cn.js'
import {
  WEEKDAYS,
  fromCalendarDate,
  longDate,
  monthGrid,
  todayCalendar,
  type CalendarDate,
} from '../../lib/dates.js'
import { Button } from './Button.js'
import { Popover } from './Popover.js'

export interface DatePickerProps {
  value: CalendarDate | null
  onChange: (value: CalendarDate | null) => void
  /** Shown when nothing is chosen. */
  placeholder?: string
  /** Nothing before this can be chosen — a class cannot start yesterday. */
  min?: CalendarDate
  max?: CalendarDate
  /** Lets the field be emptied. Off by default: most dates here are required. */
  clearable?: boolean
  invalid?: boolean
  disabled?: boolean
  id?: string
  'aria-describedby'?: string
}

/**
 * A day, chosen from a calendar.
 *
 * **A date only — never a time.** 9.6a is explicit about why: *"The start time is
 * its own field. It was folded into the date, and the field showed only the half
 * before the comma and saved that back, so opening a class and saving it unchanged
 * threw the time off the row."* `TimePicker` is the other half, and `toInstant`
 * joins them at the edge.
 *
 * Written rather than installed. A calendar is a month grid, two arrows and a
 * bounds check; a date library is 12KB and a second opinion about what a week
 * starts on.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Choose a date',
  min,
  max,
  clearable,
  invalid,
  disabled,
  id,
  ...aria
}: DatePickerProps) {
  const [open, setOpen] = useState(false)
  const anchor = value ? fromCalendarDate(value) : new Date()
  const [view, setView] = useState({ year: anchor.getFullYear(), month: anchor.getMonth() })
  const today = todayCalendar()

  /**
   * Opens on the month the date is in, every time.
   *
   * `useState` runs once, so a form that fills itself in after the request
   * lands — which every editor does — left the calendar showing whatever
   * month it had guessed while the field was empty. Somebody opening it to
   * check a date in March was shown September.
   */
  const shownFor = value ?? ''
  const [openedOn, setOpenedOn] = useState(shownFor)
  if (open && openedOn !== shownFor) {
    setOpenedOn(shownFor)
    setView({ year: anchor.getFullYear(), month: anchor.getMonth() })
  }

  /**
   * A month and a year to jump to, rather than two arrows and patience.
   *
   * The header was a label. Reaching a date eight months out meant eight
   * presses of the same chevron, on a desk that types dates all day — and
   * there was no way at all to reach another year without twelve more.
   *
   * The range is bounded by `min` and `max` where they are given, and
   * otherwise runs five years each way, which covers a class being scheduled
   * and an event being recorded after the fact.
   */
  const thisYear = new Date().getFullYear()
  const firstYear = min ? Number(min.slice(0, 4)) : thisYear - 5
  const lastYear = max ? Number(max.slice(0, 4)) : thisYear + 5
  const years = Array.from(
    { length: Math.max(1, lastYear - firstYear + 1) },
    (_, i) => firstYear + i,
  )

  const blocked = (d: CalendarDate) => Boolean((min && d < min) || (max && d > max))
  const step = (by: number) => {
    const next = new Date(view.year, view.month + by, 1)
    setView({ year: next.getFullYear(), month: next.getMonth() })
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-invalid={invalid || undefined}
          {...aria}
          className={cn(
            'flex h-10 w-full items-center gap-2 rounded-md border bg-surface px-4 text-left text-body',
            'outline-none transition-colors focus-visible:border-coral-fill focus-visible:ring-2 focus-visible:ring-coral-fill/25',
            'disabled:bg-sunk disabled:text-ink-muted disabled:cursor-not-allowed',
            invalid ? 'border-error' : 'border-hairline-strong',
            !value && 'text-ink-muted',
          )}
        >
          <Calendar className="size-4 shrink-0 text-ink-muted" aria-hidden />
          <span className="flex-1 truncate">{value ? longDate(value) : placeholder}</span>
        </button>
      }
    >
      <div className="w-64">
        <div className="flex items-center justify-between gap-1 pb-2">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="The month before"
            onClick={() => step(-1)}
          >
            <ChevronLeft />
          </Button>
          <div className="flex flex-1 items-center justify-center gap-1">
            <select
              aria-label="Month"
              value={view.month}
              onChange={(e) => setView({ ...view, month: Number(e.target.value) })}
              className="rounded-sm bg-transparent px-1 py-0.5 text-second font-medium text-ink outline-none hover:bg-sunk focus-visible:ring-2 focus-visible:ring-coral-fill/30"
            >
              {MONTHS.map((m, i) => (
                <option key={m} value={i}>
                  {m}
                </option>
              ))}
            </select>
            <select
              aria-label="Year"
              value={view.year}
              onChange={(e) => setView({ ...view, year: Number(e.target.value) })}
              className="rounded-sm bg-transparent px-1 py-0.5 text-second font-medium text-ink outline-none hover:bg-sunk focus-visible:ring-2 focus-visible:ring-coral-fill/30"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="The month after"
            onClick={() => step(1)}
          >
            <ChevronRight />
          </Button>
        </div>

        <div className="grid grid-cols-7 gap-1">
          {WEEKDAYS.map((d, i) => (
            <span key={i} className="py-1 text-center text-micro text-ink-muted" aria-hidden>
              {d}
            </span>
          ))}
          {monthGrid(view.year, view.month).map((day, i) =>
            day === null ? (
              <span key={i} />
            ) : (
              <button
                key={day}
                type="button"
                disabled={blocked(day)}
                /* Named, because "24" read out of a grid is a number and not
                   a date — and the month it belongs to is only in a heading
                   somebody using a screen reader has already passed. */
                aria-label={longDate(day)}
                aria-current={day === today ? 'date' : undefined}
                aria-pressed={day === value}
                onClick={() => {
                  onChange(day)
                  setOpen(false)
                }}
                className={cn(
                  'size-8 rounded-sm text-second outline-none transition-colors',
                  'hover:bg-sunk focus-visible:ring-2 focus-visible:ring-coral-fill/30',
                  'disabled:opacity-30 disabled:pointer-events-none',
                  day === value && 'bg-coral-fill text-on-coral hover:bg-coral-fill-hover',
                  /* Today is outlined rather than filled: filling it would make an
                     unchosen field look chosen. */
                  day !== value && day === today && 'ring-1 ring-inset ring-coral-fill',
                )}
              >
                {Number(day.slice(8))}
              </button>
            ),
          )}
        </div>

        {clearable && value && (
          <Button
            variant="tertiary"
            size="sm"
            full
            className="mt-2"
            onClick={() => {
              onChange(null)
              setOpen(false)
            }}
          >
            Clear the date
          </Button>
        )}
      </div>
    </Popover>
  )
}

/**
 * The twelve, for the jump-to control.
 *
 * Derived rather than written out, so they are the reader's own locale's
 * names rather than twelve English strings in a file.
 */
const MONTHS = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleDateString('en-CA', { month: 'long' }),
)
